/** Pure link-target construction for the secondary-review → map deep-link
 *  (UX Stage 1, C1 / decision D-7).
 *
 *  A review row locates on the map via a transformer: the derived root when the
 *  component is rooted, else the suggested candidate — `derived_root` source
 *  first (matching the dashboard's green accept-button suggestion), then the
 *  first candidate. Fully unrooted rows with no candidates return `null` so the
 *  UI renders a disabled affordance instead of a dead link.
 *
 *  Uses the same `/?feature=<Type>:<id>` pattern as discrepancyLink.ts, read by
 *  App.tsx via parseFeatureParam into the map detail panel. */

import type { ReviewQueueItem } from '../types/secondaryReview.ts'

/** The transformer id a review row should locate on the map, or `null` when
 *  the row has neither a derived root nor any candidate. */
export function secondaryReviewMapTargetId(
  item: Pick<ReviewQueueItem, 'transformer_id' | 'candidates'>,
): number | null {
  if (item.transformer_id != null) return item.transformer_id
  const suggested =
    item.candidates.find(c => c.source === 'derived_root') ?? item.candidates[0]
  return suggested?.transformer_id ?? null
}

/** The map deep-link path for a review row, or `null` when nothing locates it. */
export function secondaryReviewMapLink(
  item: Pick<ReviewQueueItem, 'transformer_id' | 'candidates'>,
): string | null {
  const id = secondaryReviewMapTargetId(item)
  return id == null ? null : `/?feature=Transformer:${id}`
}
